import { faArrowsLeftRightToLine } from "@fortawesome/pro-regular-svg-icons/faArrowsLeftRightToLine";
import { faShuffle } from "@fortawesome/pro-regular-svg-icons/faShuffle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Checkbox, FormHelperText, Grid, IconButton, Radio, Stack, TextField, Tooltip, useTheme } from "@mui/material";
import { MprcButton, MprcIconButtonMoreOptionsDropdown, MprcStringToHtml, MprcText } from "components";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcNumberField } from "components/MprcNumberField";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedQuestionAlternativeDto, ManagedQuestionResponse, QuestionType } from "generated/CoreApi_v1";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { ALTERNATIVE_FALSE, ALTERNATIVE_TRUE, MAX_ALTERNATIVES } from "views/assessment/common/assessment";
import { useQuestionContext } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { PointTypedButton } from "views/assessment/questionBank/components/QuestionCommon";

////////////////////////////////////////////////////////////////////////////////

export interface IPostValidationAlternatives {
    alternatives: string[] | undefined;
}

export interface IValidationFieldsAlternative {
    alternatives: boolean;
}

///
export function validateAlternatives(dto: ManagedQuestionResponse, forSurvey: boolean): IValidationFieldsAlternative {
    return {
        alternatives: !dto.alternatives.some(x => x.text == undefined || x.text.length == 0)
            &&
            (dto.alternatives.some(x => x.evaluation?.correctAnswer == 'T') || forSurvey)
    };
}

type QuestionAlternativeContextMenuOptions = "moveUp" | "moveDown" | "remove";

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    canSelectType: boolean;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditQuestionAlternative: React.FC<IPros> = (props) => {

    const { canSelectType } = props;
    const { viewData, postValidation, forSurvey, readonly, handleUpdateRecord, handleUpdateEvaluation, handleUpdateSurvey } = useQuestionContext();

    const { t } = useTranslation("assessment");
    const theme = useTheme();

    const currentPostValidation = postValidation.alternativeBox;

    ///
    const changeCorrectAnswer = (alternativeId: string, checked: boolean) => {
        var newAlternatives = [...viewData.alternatives];
        newAlternatives.forEach((v) => {
            if (v.alternativeId == alternativeId && v.evaluation) {
                v.evaluation.correctAnswer = checked ? ALTERNATIVE_TRUE : ALTERNATIVE_FALSE;
            }
            else if (viewData.questionType == QuestionType.SimpleChoice && v.evaluation) v.evaluation.correctAnswer = ALTERNATIVE_FALSE;
        });
        handleUpdateRecord("alternatives", newAlternatives);
    };

    ///
    const handleAddAlternative = () => {
        var newAlternativeId = uuidv4();
        if (viewData.alternatives.length < MAX_ALTERNATIVES) {
            handleUpdateRecord("alternatives",
                [
                    ...viewData.alternatives,
                    { alternativeId: newAlternativeId, text: "", displayOrder: 1, evaluation: { feedback: null, correctAnswer: ALTERNATIVE_FALSE, answerIsNumber: false }, survey: { points: null } },
                ]
            );

            setTimeout(() => {
                const el = document.getElementById(newAlternativeId);
                if (el) el.focus();
            }, 300);
        }
    };

    ///
    const handleSelectAlternativeOption = (option: QuestionAlternativeContextMenuOptions, alternative: ManagedQuestionAlternativeDto, index: number) => {
        switch (option) {
            case "remove":
                removeAlternative(alternative.alternativeId);
                break;
            case "moveUp":
                moveAlternative(index, "up");
                break;
            case "moveDown":
                moveAlternative(index, "down");
                break;
        }
    };

    ///
    const removeAlternative = (id: string) => {
        var newAlternatives = viewData.alternatives.filter(x => x.alternativeId != id);
        handleUpdateRecord("alternatives", newAlternatives);
    };

    ///
    const moveAlternative = (index: number, moveTo: "up" | "down") => {
        var newAlternatives = [...viewData.alternatives];
        var element = newAlternatives[index];
        newAlternatives.splice(index, 1);
        newAlternatives.splice(index + (moveTo == "up" ? -1 : 1), 0, element);
        handleUpdateRecord("alternatives", newAlternatives);
    };

    ///
    const getQuestionAlternativeMenuItems = useCallback(
        (item: ManagedQuestionAlternativeDto, index: number): MprcDropdownItems<QuestionAlternativeContextMenuOptions> => {

            let group0: MprcDropdownItems<QuestionAlternativeContextMenuOptions> = [];
            if (index != 0)
                group0.push({ text: t("Question.Edit.Alternative.MoveUpAction"), value: "moveUp" });
            if (index != viewData.alternatives.length - 1)
                group0.push({ text: t("Question.Edit.Alternative.MoveDownAction"), value: "moveDown" });

            let group1: MprcDropdownItems<QuestionAlternativeContextMenuOptions> = [];
            if (viewData.alternatives.length > 2 && viewData.changeVersion == true) {
                group1.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" });
            }

            return MprcDropdownBuildGroupedItems<QuestionAlternativeContextMenuOptions>([group0, group1]);
        }
        , [viewData.alternatives, viewData.changeVersion]
    );

    ///
    const RenderEvaluationOptions = () => {
        return (
            <Grid container>
                <Grid size="grow">
                    <MprcSwitch
                        label={t("Question.Edit.Alternative.QuestionTypeLabel")}
                        checked={viewData.questionType == QuestionType.MultipleChoice}
                        disabled={readonly || !canSelectType}
                        onChange={(e) => handleUpdateRecord("questionType",
                            e.target.checked
                                ? QuestionType.MultipleChoice
                                : QuestionType.SimpleChoice)
                        }
                    />
                </Grid>
                <Grid>
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            title={t("Question.Edit.Alternative.RandomizeTooltip")}
                            onClick={() => handleUpdateEvaluation("randomized", !viewData.evaluation?.randomized)}
                            disabled={readonly}>
                            <FontAwesomeIcon
                                icon={faShuffle}
                                color={viewData.evaluation?.randomized ? theme.palette.highlight.main : theme.palette.muted.main}
                                fixedWidth
                            />
                        </IconButton>
                    </Stack>
                </Grid>
            </Grid >
        );
    };

    ///
    const RenderSurveyOption = () => {
        return (
            <Grid container justifyContent="end">
                <Grid size="grow">
                    <MprcSwitch
                        label={t("Question.Edit.Alternative.QuestionTypeLabel")}
                        checked={viewData.questionType == QuestionType.MultipleChoice}
                        disabled={readonly || !canSelectType}
                        onChange={(e) => handleUpdateRecord("questionType",
                            e.target.checked
                                ? QuestionType.MultipleChoice
                                : QuestionType.SimpleChoice)
                        }
                    />
                </Grid>
                <Grid>
                    <Stack direction="row" spacing={1}>
                        <PointTypedButton
                            tooltip={t("Question.Edit.Alternative.PointTypedTooltip")}
                            active={viewData.survey?.pointTyped ?? false}
                            readonly={readonly}
                            onClick={() => handleUpdateSurvey("pointTyped", !viewData.survey?.pointTyped)}
                        />
                        <IconButton
                            title={t("Question.Edit.Alternative.ShowSideBySideTooltip")}
                            onClick={() => handleUpdateSurvey("showSideBySide", !viewData.survey?.showSideBySide)}
                            disabled={readonly}>
                            <FontAwesomeIcon
                                icon={faArrowsLeftRightToLine}
                                color={viewData.survey?.showSideBySide ? theme.palette.highlight.main : theme.palette.muted.main}
                                fixedWidth
                            />
                        </IconButton>
                    </Stack>
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderMultiChoiceOptions = () => {
        return (
            <>
                {
                    forSurvey
                        ? <RenderSurveyOption />
                        : <RenderEvaluationOptions />
                }
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <RenderMultiChoiceOptions />
            {
                viewData.alternatives.map((v, index) => {
                    return (
                        <Grid key={v.alternativeId} container alignContent="center" alignItems="flex-start" spacing={1} mt={0.5}>
                            <Grid>
                                {
                                    viewData.questionType == QuestionType.SimpleChoice
                                        ?
                                        (
                                            forSurvey
                                                ?
                                                <Tooltip title={t("Question.Edit.Alternative.SurveyTooltip")}>
                                                    <span>
                                                        <Radio disabled />
                                                    </span>
                                                </Tooltip>
                                                :
                                                <Radio
                                                    value={v.alternativeId}
                                                    checked={v.evaluation?.correctAnswer == 'T'}
                                                    disabled={readonly}
                                                    onChange={
                                                        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                                            changeCorrectAnswer(v.alternativeId, checked);
                                                        }
                                                    }
                                                />
                                        )
                                        :
                                        <Checkbox
                                            value={v.alternativeId}
                                            checked={v.evaluation?.correctAnswer == 'T'}
                                            disabled={readonly || forSurvey}
                                            onChange={
                                                (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                                    changeCorrectAnswer(v.alternativeId, checked);
                                                }
                                            }
                                        />
                                }
                            </Grid>
                            <Grid size="grow">
                                <TextField
                                    id={v.alternativeId}
                                    fullWidth
                                    placeholder={t("Question.Edit.Alternative.TextPlaceholder")}
                                    value={viewData.alternatives[index].text}
                                    onChange={(e) => {
                                        var newAlternatives = [...viewData.alternatives];
                                        newAlternatives[index].text = e.target.value;
                                        handleUpdateRecord("alternatives", newAlternatives);
                                    }}
                                    disabled={readonly}
                                    slotProps={{
                                        input: {
                                            inputProps: { maxLength: 512 }
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key == 'Enter') {
                                            if (index < viewData.alternatives.length - 1) {
                                                setTimeout(() => {
                                                    const el = document.getElementById(viewData.alternatives[index + 1].alternativeId);
                                                    if (el) el.focus();
                                                }, 100);
                                            }
                                            else if (viewData.alternatives[index].text.length > 0 && viewData.alternatives.length < MAX_ALTERNATIVES) {
                                                handleAddAlternative();
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            {
                                forSurvey && viewData.survey?.pointTyped &&
                                <Grid>
                                    <MprcNumberField
                                        value={viewData.alternatives[index].survey?.points}
                                        sx={{ width: 100 }}
                                        allowNegative
                                        onChange={value => {
                                            var newAlternatives = [...viewData.alternatives];
                                            newAlternatives[index].survey!.points = value;
                                            handleUpdateRecord("alternatives", newAlternatives);
                                        }}
                                        slotProps={{ input: { endAdornment: t("Question.Edit.Alternative.PointsLabel") } }}
                                    />
                                </Grid>
                            }
                            <Grid>
                                {
                                    readonly != true &&
                                    <MprcIconButtonMoreOptionsDropdown
                                        color="muted"
                                        onChange={(selection) => handleSelectAlternativeOption(selection, v, index)}
                                        items={getQuestionAlternativeMenuItems(v, index)}
                                    />
                                }
                            </Grid>
                        </Grid>
                    );
                })
            }
            {
                !!currentPostValidation.alternatives &&
                <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.alternatives} />}</FormHelperText>
            }
            <Box ml={4} mt={1} >
                {
                    readonly != true &&
                    <MprcButton
                        onClick={() => handleAddAlternative()}
                        disabled={viewData.alternatives.length >= MAX_ALTERNATIVES || viewData.changeVersion == false}
                    >
                        + {t("Question.Edit.Alternative.AddAlternativeButtonText")}
                    </MprcButton>
                }
            </Box>
        </>
    );
};
